import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Copy } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const JobTemplateManager = ({ token, onUseTemplate }) => {
  const [templates, setTemplates] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    template_name: '',
    job_type: '',
    client_name: '',
    location: '',
    typical_time: '',
    typical_wage: '',
    typical_job_value: '',
    notes: ''
  });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/job-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const createTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/job-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newTemplate,
          typical_wage: parseFloat(newTemplate.typical_wage) || null,
          typical_job_value: parseFloat(newTemplate.typical_job_value) || null
        })
      });

      if (response.ok) {
        await fetchTemplates();
        setShowCreateForm(false);
        setNewTemplate({
          template_name: '',
          job_type: '',
          client_name: '',
          location: '',
          typical_time: '',
          typical_wage: '',
          typical_job_value: '',
          notes: ''
        });
      } else {
        alert('Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      alert('Error creating template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/job-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchTemplates();
      } else {
        alert('Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Error deleting template');
    }
  };

  const useTemplate = async (templateId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/job-templates/${templateId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const template = await response.json();
        onUseTemplate(template);
      }
    } catch (error) {
      console.error('Error using template:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Job Templates</CardTitle>
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            New Template
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showCreateForm && (
          <div className="mb-4 p-4 border rounded-lg bg-gray-50">
            <h3 className="font-semibold mb-3">Create New Template</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                placeholder="Template Name (e.g., Smith Wedding)"
                value={newTemplate.template_name}
                onChange={(e) => setNewTemplate({...newTemplate, template_name: e.target.value})}
              />
              <Input
                placeholder="Job Type"
                value={newTemplate.job_type}
                onChange={(e) => setNewTemplate({...newTemplate, job_type: e.target.value})}
              />
              <Input
                placeholder="Client Name"
                value={newTemplate.client_name}
                onChange={(e) => setNewTemplate({...newTemplate, client_name: e.target.value})}
              />
              <Input
                placeholder="Location"
                value={newTemplate.location}
                onChange={(e) => setNewTemplate({...newTemplate, location: e.target.value})}
              />
              <Input
                placeholder="Typical Time (e.g., 6:00 PM)"
                value={newTemplate.typical_time}
                onChange={(e) => setNewTemplate({...newTemplate, typical_time: e.target.value})}
              />
              <Input
                type="number"
                placeholder="Typical Wage (£)"
                value={newTemplate.typical_wage}
                onChange={(e) => setNewTemplate({...newTemplate, typical_wage: e.target.value})}
              />
              <Input
                type="number"
                placeholder="Typical Job Value (£)"
                value={newTemplate.typical_job_value}
                onChange={(e) => setNewTemplate({...newTemplate, typical_job_value: e.target.value})}
              />
              <Input
                placeholder="Notes"
                value={newTemplate.notes}
                onChange={(e) => setNewTemplate({...newTemplate, notes: e.target.value})}
                className="md:col-span-2"
              />
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={createTemplate}>Save Template</Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {templates.length === 0 && (
            <p className="text-gray-500 text-center py-4">No templates yet. Create one to save time!</p>
          )}
          {templates.map(template => (
            <div key={template.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
              <div className="flex-1">
                <h4 className="font-semibold">{template.template_name}</h4>
                <p className="text-sm text-gray-600">
                  {template.client_name} • {template.job_type} • {template.location}
                </p>
                {template.use_count > 0 && (
                  <p className="text-xs text-gray-500">Used {template.use_count} times</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => useTemplate(template.id)}
                  className="flex items-center gap-1"
                >
                  <Copy size={14} />
                  Use
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteTemplate(template.id)}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default JobTemplateManager;